// Vista del kine: catálogo de ejercicios disponibles (globales + custom).
// R-only en este bloque — Alta/Modificación/Baja deferidas a Sprint 3
// según `docs/decisions/plan-post-piloto.md`. Razón: el detector
// requiere schema JSON calibrado (en filesystem, importado al bundle),
// y permitir Alta sin schema crearía "ejercicios fantasma" no
// prescriptibles. La calibración necesita workflow dev-led, no UI.
//
// Showcase: se sacó el botón "+ Nuevo ejercicio" (estaba disabled).

import { useState } from "react";
import { Dumbbell } from "lucide-react";

import { KineHeader } from "../components/kine/KineHeader";
import { KineSidebar } from "../components/kine/KineSidebar";
import { useKineProfile } from "@/lib/supabase/queries/useKineProfile";
import {
  useEjercicios,
  type Ejercicio,
  type ViewAngle,
} from "@/lib/supabase/queries/useEjercicios";
import { getExerciseSchema } from "@/lib/pose/exerciseSchemas";
import { PlayableThumbnail } from "../components/patients/PlayableThumbnail";
import { ExerciseStatusBadges } from "../components/patients/ExerciseStatusBadges";
import { VideoTutorialModal } from "../components/sessions/VideoTutorialModal";

const VIEW_ANGLE_LABEL: Record<ViewAngle, string> = {
  frontal: "Frontal",
  lateral_left: "Lateral izq.",
  lateral_right: "Lateral der.",
  flexible: "Flexible",
};

export function BibliotecaEjercicios() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Video activo del modal de tutorial. null = modal cerrado. Se
  // actualiza al click del thumbnail de cualquier card del grid.
  const [activeVideo, setActiveVideo] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const { data: kineProfile } = useKineProfile();
  const { data: ejercicios, error, loading } = useEjercicios();

  const totalLabel = ejercicios
    ? `${ejercicios.length} ${ejercicios.length === 1 ? "ejercicio disponible" : "ejercicios disponibles"}`
    : "Cargando catálogo…";

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={kineProfile?.nombre || "—"}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="biblioteca"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-6">
          <div className="max-w-[1200px]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl">Biblioteca de ejercicios</h1>
                <p className="text-muted-foreground mt-1.5 text-sm">{totalLabel}</p>
              </div>
            </div>

            {loading && <LoadingState />}
            {error && <ErrorState message={error} />}
            {!loading && !error && ejercicios && ejercicios.length === 0 && (
              <EmptyState />
            )}
            {!loading && !error && ejercicios && ejercicios.length > 0 && (
              <ExerciseGrid
                ejercicios={ejercicios}
                onPlay={(url, name) => setActiveVideo({ url, name })}
              />
            )}
          </div>
        </div>
      </div>

      <VideoTutorialModal
        videoUrl={activeVideo?.url}
        exerciseName={activeVideo?.name ?? ""}
        open={activeVideo !== null}
        onClose={() => setActiveVideo(null)}
      />
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <p className="text-center text-muted-foreground py-12">
      Cargando catálogo…
    </p>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 border border-border text-center">
      <p className="text-orange-600 font-semibold mb-2">
        No pudimos cargar el catálogo
      </p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-card rounded-2xl p-8 border border-border text-center">
      <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
        <Dumbbell className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        El catálogo está vacío
      </h2>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Cuando se agregue el primer ejercicio al catálogo (vía migration o
        seed), va a aparecer acá.
      </p>
    </div>
  );
}

function ExerciseGrid({
  ejercicios,
  onPlay,
}: {
  ejercicios: Ejercicio[];
  onPlay: (videoUrl: string, exerciseName: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {ejercicios.map((ej) => (
        <ExerciseCard key={ej.id} ejercicio={ej} onPlay={onPlay} />
      ))}
    </div>
  );
}

function ExerciseCard({
  ejercicio,
  onPlay,
}: {
  ejercicio: Ejercicio;
  onPlay: (videoUrl: string, exerciseName: string) => void;
}) {
  // Resolver schema del bundle por id — para thumbnail YouTube + badges
  // de estado (calibration preliminary, video_only). Si el schema NO
  // está en el registry (caso de drift DB ↔ bundle), igual renderea
  // el card con placeholder + sin badges (degradación graceful).
  const schema = getExerciseSchema(ejercicio.id);
  const videoUrl = schema?.video_url;

  return (
    <div className="bg-card rounded-xl p-4 border border-border shadow-sm flex flex-col transition-all duration-200 ease-out hover:shadow-md hover:border-primary/30">
      <div className="mb-3">
        <PlayableThumbnail
          videoUrl={videoUrl}
          exerciseName={ejercicio.nombre}
          onClick={
            videoUrl ? () => onPlay(videoUrl, ejercicio.nombre) : undefined
          }
          className="aspect-video"
        />
      </div>

      <div className="flex items-start gap-2 mb-1">
        <h3 className="font-semibold text-base flex-1 min-w-0">
          {ejercicio.nombre}
        </h3>
      </div>

      {/* Badges de estado (calibración preliminar + sin cámara si video_only).
          Apilados horizontalmente debajo del título — discreto, no dominante. */}
      <ExerciseStatusBadges schema={schema} />

      {ejercicio.descripcion && (
        <p className="text-sm text-muted-foreground mt-2 mb-3 line-clamp-2">
          {ejercicio.descripcion}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {ejercicio.region_anatomica && (
          <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full font-semibold capitalize">
            {ejercicio.region_anatomica}
          </span>
        )}
        <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full font-medium">
          {VIEW_ANGLE_LABEL[ejercicio.view_angle]}
        </span>
        <OrigenBadge createdByKineId={ejercicio.created_by_kine_id} />
      </div>

      {ejercicio.setup_hint && (
        <p className="text-xs text-muted-foreground mb-3 italic line-clamp-2">
          {ejercicio.setup_hint}
        </p>
      )}
    </div>
  );
}

function OrigenBadge({
  createdByKineId,
}: {
  createdByKineId: string | null;
}) {
  if (createdByKineId === null) {
    return (
      <span className="text-xs px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full font-medium">
        Catálogo global
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 bg-accent/15 text-accent rounded-full font-medium">
      Custom
    </span>
  );
}
